/**
 * Centralized filter configuration for ProblemsTable
 * Uses only API-provided filter values for consistency across all pages
 */

import React from 'react';
import TableFilterDropdown from '../shared/TableFilterDropdown';

/**
 * Get filter values from API data only - no fallback to page-local data
 */
export const getFilterValues = (filterData) => {
  if (!filterData) {
    return {
      group: [],
      location: [],
      machineId: [],
      product: [],
      storeCluster: [],
      category: [],
      productCategory: [],
      analysisType: [],
      problemType: [],
      sector: [],
      severity: [],
      route: []
    };
  }
  
  const result = {
    group: filterData.group || [],
    location: filterData.location || [],
    machineId: filterData.machineId || [],
    product: filterData.products || [],
    storeCluster: filterData.storeCluster || [],
    category: filterData.category || [],
    productCategory: filterData.productCategory || [],
    analysisType: filterData.analysisType || [],
    // Build problemType from union of category and analysisType (backend uses codes like seasonal_underperformance)
    problemType: Array.from(new Set([...
      (filterData.category || []),
      ...(filterData.analysisType || [])
    ])),
    sector: filterData.sector || [],
    severity: filterData.severity || [],
    route: filterData.routes || []
  };
  
  return result;
};

/**
 * Create a filter dropdown renderer for a specific filter type
 */
export const createFilterDropdown = (filterKey, filterValues, columnName, filtersLoading, t) => {
  return (props) => {
    // Transform values to the format expected by TableFilterDropdown
    let values = [];
    const rawValues = Array.isArray(filterValues[filterKey]) ? filterValues[filterKey] : [];

    if (filterKey === 'problemType') {
      // Map backend codes to localized labels while keeping original value for filtering
      const labelForProblemType = (code) => {
        switch (code) {
          case 'seasonal_underperformance':
            return t('seasonal_underperformance_type');
          case 'low_turnover':
            return t('low_turnover_type');
          case 'zero_sales':
            return t('zero_sales_periods_type');
          case 'declining_trend':
            return t('declining_sales_trends_type');
          // Orquestador. Sin estos casos las casillas enseñaban 'orchestrator_precio' en crudo
          // justo al lado de "Bajo Rendimiento" ya humanizado.
          case 'orchestrator_precio':
            return t('orchestrator_precio_type');
          case 'orchestrator_reemplazo':
            return t('orchestrator_reemplazo_type');
          case 'orchestrator_ampliar_carril':
            return t('orchestrator_ampliar_carril_type');
          case 'manual_ampliar_carril':
            return t('manual_ampliar_carril_type');
          default:
            return code;
        }
      };
      values = rawValues.map(value => ({ text: labelForProblemType(value), value }));
    } else if (filterKey === 'route') {
      values = rawValues.map(item => ({
        text: item?.name || item?.code || String(item?.id ?? item),
        value: String(item?.id ?? item?.value ?? item?.name ?? item)
      }));
    } else {
      values = rawValues.map(value => ({ text: value, value }));
    }
    
    return (
      <TableFilterDropdown
        type="checkbox"
        values={values}
        columnName={columnName}
        loading={filtersLoading}
        {...props}
      />
    );
  };
};

/**
 * Filter configuration mapping
 * Defines the relationship between filter keys and their API/data field mappings
 */
export const FILTER_CONFIG = {
  group: {
    apiKey: 'group',
    extractKey: 'group'
  },
  location: {
    apiKey: 'location',
    extractKey: 'location'
  },
  machineId: {
    apiKey: 'machineId',
    extractKey: 'machineId'
  },
  product: {
    apiKey: 'products',
    extractKey: 'product'
  },
  storeCluster: {
    apiKey: 'storeCluster',
    extractKey: 'storeCluster'
  },
  category: {
    apiKey: 'category', 
    extractKey: 'category'
  },
  productCategory: {
    apiKey: 'productCategory',
    extractKey: 'productCategory'
  },
  analysisType: {
    apiKey: 'analysisType',
    extractKey: 'analysisType'
  },
  sector: {
    apiKey: 'sector',
    extractKey: 'sector'
  },
  severity: {
    apiKey: 'severity',
    extractKey: 'severity'
  },
  route: {
    apiKey: 'routes',
    extractKey: 'route'
  }
};
